/**
 * Plans v2 local caps: API workspaces kept on this machine BESIDES the
 * Personal one (owner, 16.10.2026: Free = "one workspace, the personal one";
 * `local_workspaces_limit` 0). The first workspace a user has, local or
 * not, is their Personal workspace and can always be made.
 */
import { useWorkspaceStore } from '../store/workspaceStore';
import { useAuthStore } from '../store/authStore';
import { isLocalWorkspace } from '../utils/workspaceStorage';
import { isTeamWorkspace } from '../utils/workspaceShareEligibility';
import { requireLocalCapacity } from './localGate';
import { capRefusal } from './capRefusal';

/**
 * The user's own workspaces (team workspaces belong to the team and are
 * neither the Personal one nor counted) and how many of them are extra LOCAL
 * ones. The Personal workspace exists as soon as the user has ANY own
 * workspace: a local one, or a backend one (`storage` null, e.g. a signed-in
 * account's Personal). Free has no cloud, so an own backend workspace IS the
 * Personal one and then every local workspace is extra; otherwise the first
 * local one is Personal.
 */
function ownWorkspaces() {
  const authed = Boolean(useAuthStore.getState?.().isAuthenticated);
  const own = (useWorkspaceStore.getState().workspaces || []).filter((w) => w && !isTeamWorkspace(w));
  const local = own.filter((w) => isLocalWorkspace(w, authed)).length;
  const personalIsBackend = own.length > local;
  return { any: own.length > 0, extra: Math.max(0, local - (personalIsBackend ? 0 : 1)) };
}

/** Local workspaces besides the Personal one. */
export function extraLocalWorkspaceCount() {
  return ownWorkspaces().extra;
}

/**
 * May one more local workspace be made? The very first workspace is always
 * the Personal one. Refused → the upgrade opens (UI create points).
 */
export function requireWorkspaceSlot() {
  const { any, extra } = ownWorkspaces();
  return !any || requireLocalCapacity('workspaces', extra);
}

/** The refusal sentence for a path that throws instead, or null. */
export function workspaceSlotRefusal() {
  const { any, extra } = ownWorkspaces();
  return !any ? null : capRefusal('workspaces', extra);
}
